import { useEffect, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

const NAV_ITEMS = [
  { to: "/", label: "Acasă", id: "acasa" },
  { to: "/meniu", label: "Meniu", id: "meniu" },
  { to: "/galerie", label: "Galerie", id: "galerie" },
  { to: "/rezervari", label: "Rezervări", id: "rezervari" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <>
      <header
        data-testid="navbar"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? "bg-brand-night/90 backdrop-blur-md border-b border-brand-gold/15"
            : "bg-gradient-to-b from-brand-night/80 via-brand-night/40 to-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
          <div className="flex items-center justify-between h-20 md:h-24">
            <Link
              to="/"
              data-testid="nav-logo"
              className="flex flex-col leading-none group"
            >
              <span className="text-[10px] tracking-[0.4em] uppercase text-brand-gold/70 font-sans">
                Bucureşti · 1932
              </span>
              <span className="font-serif text-2xl md:text-[28px] text-brand-gold tracking-wide italic group-hover:text-brand-goldHover transition-colors">
                La Maison Dorée
              </span>
            </Link>

            {/* Desktop nav */}
            <nav className="hidden lg:flex items-center gap-10">
              {NAV_ITEMS.map((item) => (
                <NavLink
                  key={item.id}
                  to={item.to}
                  end={item.to === "/"}
                  data-testid={`nav-link-${item.id}`}
                  className={({ isActive }) =>
                    `relative font-sans text-[13px] tracking-[0.25em] uppercase transition-colors duration-300 py-2 ${
                      isActive
                        ? "text-brand-gold"
                        : "text-brand-ivory/80 hover:text-brand-gold"
                    } after:absolute after:left-0 after:right-0 after:-bottom-0.5 after:h-px after:bg-brand-gold after:transition-transform after:duration-500 after:origin-left ${
                      isActive ? "after:scale-x-100" : "after:scale-x-0 hover:after:scale-x-100"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
              <Link
                to="/rezervari"
                data-testid="nav-cta-reserve"
                className="ml-2 inline-flex items-center justify-center px-6 py-3 border border-brand-gold text-brand-gold font-sans text-[11px] tracking-[0.3em] uppercase hover:bg-brand-gold hover:text-brand-night transition-all duration-300"
              >
                Rezervă
              </Link>
            </nav>

            {/* Mobile toggle */}
            <button
              type="button"
              data-testid="nav-mobile-toggle"
              onClick={() => setOpen(true)}
              aria-label="Deschide meniul"
              className="lg:hidden text-brand-gold p-2 -mr-2"
            >
              <Menu size={26} strokeWidth={1.4} />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile full-screen overlay */}
      <div
        className={`fixed inset-0 z-[60] bg-brand-night/98 backdrop-blur-sm transition-opacity duration-500 lg:hidden ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        data-testid="nav-mobile-overlay"
      >
        <div className="flex items-center justify-between h-20 px-6">
          <span className="font-serif italic text-2xl text-brand-gold">La Maison Dorée</span>
          <button
            type="button"
            data-testid="nav-mobile-close"
            onClick={() => setOpen(false)}
            aria-label="Închide meniul"
            className="text-brand-gold p-2 -mr-2"
          >
            <X size={28} strokeWidth={1.4} />
          </button>
        </div>
        <nav className="flex flex-col items-center justify-center gap-8 pt-16">
          {NAV_ITEMS.map((item, i) => (
            <NavLink
              key={item.id}
              to={item.to}
              end={item.to === "/"}
              data-testid={`nav-mobile-link-${item.id}`}
              className={({ isActive }) =>
                `font-serif text-3xl tracking-wide transition-colors duration-300 ${
                  isActive ? "text-brand-gold underline underline-offset-8 decoration-1" : "text-brand-ivory/90 hover:text-brand-gold"
                }`
              }
              style={{ transitionDelay: `${i * 60}ms` }}
            >
              {item.label}
            </NavLink>
          ))}
          <div className="mt-8 flex flex-col items-center gap-3 text-brand-ivory/60 text-sm font-sans tracking-wider">
            <span>+40 721 234 567</span>
            <span>Strada Franceză 14, Bucureşti</span>
          </div>
        </nav>
      </div>
    </>
  );
}
